import React, { useState, useEffect, useMemo } from 'react';
import { io } from 'socket.io-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faDatabase, 
  faServer, 
  faSync,
  faClock,
  faMemory,
  faHdd,
  faMicrochip,
  faBolt,
  faExclamationTriangle,
  faCheckCircle,
  faUserCircle,
  faTachometerAlt,
  faBroom,
  faPowerOff,
  faShieldAlt,
  faNetworkWired,
  faMobileAlt,
  faDesktop,
  faLaptop,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faStethoscope,
  faListAlt,
  faSkullCrossbones,
  faTrashAlt,
  faPlay,
  faCopy,
  faToggleOn,
  faToggleOff,
  faExclamationCircle,
  faTimesCircle,
  faInfoCircle,
  faWrench,
  faLayerGroup,
  faExpand,
  faCompress,
  faList,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import './TempsReel.css';

const TempsReel = () => {
  const [activeUsers, setActiveUsers] = useState([]);
  const [dbPerformance, setDbPerformance] = useState({
    connections: { active: 0, max: 100, usage: 0 },
    queries: { slow: 0, total: 0, avgTime: null },
    size: { total: '0 MB', tables: [] },
    cache: { hitRatio: 0, status: 'normal' }
  });
  const [serverPerformance, setServerPerformance] = useState({
    host: '',
    uptime: 0,
    cpu: { usage: 0, load: 0, cores: 0, temp: null },
    memory: { used: 0, total: 0, usedGB: 0, totalGB: 0, percentage: 0 },
    disk: { used: 0, total: 0, usedGB: 0, totalGB: 0, percentage: 0, mount: '/' },
    performance: { score: 0, cacheHitRatio: 0, activeConnections: 0, maxConnections: 100, status: 'normal' },
    status: 'online'
  });
  const [queryMetrics, setQueryMetrics] = useState({
    fetchTime: 0,
    timestamp: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Live visitors to the public Accueil page
  const [liveVisits, setLiveVisits] = useState([]);
  const MAX_LIVE_VISITS = 20;
  
  // User list filters and pagination
  const [userSearch, setUserSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, online, offline
  const [sourceFilter, setSourceFilter] = useState('all'); // all, web, mobile, electron
  const [currentPage, setCurrentPage] = useState(1);
  const USERS_PER_PAGE = 5;

  // Expandable user detail
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [expandedUserGroups, setExpandedUserGroups] = useState([]);
  const [loadingUserGroups, setLoadingUserGroups] = useState(false);

  // Users view mode: 'list' (table) or 'espace' (grouped by espace, full-screen)
  const [usersViewMode, setUsersViewMode] = useState('espace');
  const [espaces, setEspaces] = useState([]);
  const [loadingEspaces, setLoadingEspaces] = useState(false);
  const [espaceSearch, setEspaceSearch] = useState('');
  const [espacePage, setEspacePage] = useState(1);
  const ESPACES_PER_PAGE = 15;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const espaceViewRef = React.useRef(null);

  // DB Management tabs
  const [dbManagementTab, setDbManagementTab] = useState('overview'); // overview, indexes, diagnostics, connections
  const [indexRecommendations, setIndexRecommendations] = useState({ recommendations: [], summary: {} });
  const [performanceDiagnostics, setPerformanceDiagnostics] = useState({ diagnostics: [], config: [], connectionHealth: [], summary: {} });
  const [connectionsDetail, setConnectionsDetail] = useState({ connections: [], summary: {} });
  const [loadingDbManagement, setLoadingDbManagement] = useState(false);
  const [autoGuardEnabled, setAutoGuardEnabled] = useState(true);
  const [autoGuardThreshold, setAutoGuardThreshold] = useState(70);
  const [autoGuardIdleMinutes, setAutoGuardIdleMinutes] = useState(5);
  const [autoGuardAlert, setAutoGuardAlert] = useState(null);
  const [executingAction, setExecutingAction] = useState(null);

  const toggleUserExpand = async (user) => {
    if (expandedUserId === user.id) {
      setExpandedUserId(null);
      setExpandedUserGroups([]);
      return;
    }
    setExpandedUserId(user.id);
    setExpandedUserGroups([]);
    setLoadingUserGroups(true);
    try {
      const response = await api.get(`/admin/users/${user.id}/groups`);
      setExpandedUserGroups(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching user groups:', err);
      setExpandedUserGroups([]);
    } finally {
      setLoadingUserGroups(false);
    }
  };

  useEffect(() => {
    fetchRealtimeData();

    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchRealtimeData, 10000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  // Live feed of visits to the public Accueil page (initial load + real-time via Socket.IO)
  useEffect(() => {
    const fetchRecentVisits = async () => {
      try {
        const response = await api.get(`/admin/analytics/recent-visits?limit=${MAX_LIVE_VISITS}`);
        setLiveVisits(response.data || []);
      } catch (err) {
        console.error('[TEMPS REEL] Error fetching recent visits:', err);
      }
    };
    fetchRecentVisits();

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://www.lonnii.com';
    const socket = io(backendUrl, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    socket.on('newVisit', (visit) => {
      setLiveVisits((prev) => [visit, ...prev].slice(0, MAX_LIVE_VISITS));
    });

    return () => socket.disconnect();
  }, []);

  const fetchEspacesData = async () => {
    setLoadingEspaces(true);
    try {
      const response = await api.get('/admin/realtime/users-by-espace');
      setEspaces(response.data?.espaces || []);
    } catch (err) {
      console.error('[TEMPS REEL] Error fetching espaces:', err);
    } finally {
      setLoadingEspaces(false);
    }
  };

  // Fetch espaces data when switching to that view, and keep it refreshed
  useEffect(() => {
    if (usersViewMode !== 'espace') return;
    fetchEspacesData();

    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchEspacesData, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [usersViewMode, autoRefresh]);

  // Track native fullscreen state to toggle the expand/compress icon
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      espaceViewRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  // Auto-guard: check connection usage every 30s
  useEffect(() => {
    if (!autoGuardEnabled) {
      setAutoGuardAlert(null);
      return;
    }

    const runAutoGuard = async () => {
      try {
        const response = await api.post('/admin/realtime/auto-guard', {
          threshold: autoGuardThreshold,
          maxIdleMinutes: autoGuardIdleMinutes
        });
        if (response.data?.alert) {
          setAutoGuardAlert(response.data.alert);
        } else {
          setAutoGuardAlert(null);
        }
      } catch (err) {
        console.error('[AUTO-GUARD] Error:', err);
      }
    };

    runAutoGuard();
    const guardInterval = setInterval(runAutoGuard, 30000);
    return () => clearInterval(guardInterval);
  }, [autoGuardEnabled, autoGuardThreshold, autoGuardIdleMinutes]);

  const fetchRealtimeData = async () => {
    try {
      setError(null);
      const response = await api.get('/admin/realtime/stats');
      
      if (response.data) {
        setActiveUsers(response.data.activeUsers || []);
        setDbPerformance(response.data.database || dbPerformance);
        setServerPerformance(response.data.server || serverPerformance);
        setQueryMetrics(response.data.queryMetrics || { fetchTime: 0, timestamp: null });
        setLastUpdate(new Date());
      }
      
      setLoading(false);
    } catch (err) {
      console.error('[TEMPS REEL] Error:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement');
      setLoading(false);
    }
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}j ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getStatusColor = (percentage) => {
    if (percentage > 90) return '#ef4444'; // Red
    if (percentage > 75) return '#f59e0b'; // Orange
    return '#10b981'; // Green
  };

  const renderCircularProgress = (percentage, label, subLabel, icon) => {
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;
    const color = getStatusColor(percentage);

    return (
      <div className="metric-card">
        <div className="metric-header">
          <div className="icon-wrapper">
            <FontAwesomeIcon icon={icon} />
          </div>
          <h3>{label}</h3>
        </div>
        <div className="circular-chart-container">
          <svg viewBox="0 0 100 100" className="circular-chart">
            <circle
              className="bg-circle"
              cx="50"
              cy="50"
              r={radius}
            />
            <circle
              className="progress-circle"
              cx="50"
              cy="50"
              r={radius}
              stroke={color}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="chart-content">
            <span className="chart-value">{percentage}%</span>
          </div>
        </div>
        <div className="metric-footer">
          {subLabel}
        </div>
      </div>
    );
  };

  const optimizeDatabase = async () => {
    try {
      await api.post('/admin/realtime/optimize-db');
      alert('Optimisation de la base de données lancée!');
      fetchRealtimeData();
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const clearCache = async () => {
    try {
      await api.post('/admin/realtime/clear-cache');
      alert('Cache vidé avec succès!');
      fetchRealtimeData();
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // ── DB Management Functions ──
  const fetchIndexRecommendations = async () => {
    setLoadingDbManagement(true);
    try {
      const response = await api.get('/admin/realtime/index-recommendations');
      setIndexRecommendations(response.data || { recommendations: [], summary: {} });
    } catch (err) {
      console.error('Error fetching index recommendations:', err);
    } finally {
      setLoadingDbManagement(false);
    }
  };

  const fetchPerformanceDiagnostics = async () => {
    setLoadingDbManagement(true);
    try {
      const response = await api.get('/admin/realtime/performance-diagnostics');
      setPerformanceDiagnostics(response.data || { diagnostics: [], config: [], connectionHealth: [], summary: {} });
    } catch (err) {
      console.error('Error fetching diagnostics:', err);
    } finally {
      setLoadingDbManagement(false);
    }
  };

  const fetchConnectionsDetail = async () => {
    setLoadingDbManagement(true);
    try {
      const response = await api.get('/admin/realtime/connections-detail');
      setConnectionsDetail(response.data || { connections: [], summary: {} });
    } catch (err) {
      console.error('Error fetching connections:', err);
    } finally {
      setLoadingDbManagement(false);
    }
  };

  const handleTabChange = (tab) => {
    setDbManagementTab(tab);
    if (tab === 'indexes') fetchIndexRecommendations();
    else if (tab === 'diagnostics') fetchPerformanceDiagnostics();
    else if (tab === 'connections') fetchConnectionsDetail();
  };

  const executeIndexCommand = async (sql) => {
    if (!window.confirm(`Exécuter cette commande ?\n\n${sql}`)) return;
    setExecutingAction(sql);
    try {
      await api.post('/admin/realtime/execute-index', { sql });
      alert('Commande exécutée avec succès!');
      fetchIndexRecommendations();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.details || err.message));
    } finally {
      setExecutingAction(null);
    }
  };

  const executeDiagnosticFix = async (sql, type) => {
    if (!window.confirm(`Appliquer cette correction ?\n\n${sql}`)) return;
    setExecutingAction(sql);
    try {
      await api.post('/admin/realtime/execute-fix', { sql, type });
      alert('Correction appliquée avec succès!');
      fetchPerformanceDiagnostics();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.details || err.message));
    } finally {
      setExecutingAction(null);
    }
  };

  const killConnection = async (pid) => {
    if (!window.confirm(`Terminer la connexion PID ${pid} ?`)) return;
    setExecutingAction(`kill-${pid}`);
    try {
      const response = await api.post('/admin/realtime/kill-connection', { pid });
      alert(response.data?.message || 'Connexion terminée');
      fetchConnectionsDetail();
      fetchRealtimeData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.details || err.message));
    } finally {
      setExecutingAction(null);
    }
  };

  const cleanupIdleConnections = async (maxIdleSeconds) => {
    const hasSeconds = maxIdleSeconds != null;
    const label = hasSeconds
      ? `${maxIdleSeconds} seconde(s)`
      : `${autoGuardIdleMinutes} minute(s)`;
    if (!window.confirm(`Nettoyer toutes les connexions inactives depuis plus de ${label} ?`)) return;
    setExecutingAction('cleanup');
    try {
      const payload = hasSeconds
        ? { maxIdleSeconds: parseInt(maxIdleSeconds) }
        : { maxIdleMinutes: autoGuardIdleMinutes };
      const response = await api.post('/admin/realtime/cleanup-idle', payload);
      alert(response.data?.message || 'Nettoyage effectué');
      fetchConnectionsDetail();
      fetchRealtimeData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.details || err.message));
    } finally {
      setExecutingAction(null);
    }
  };

  const copySql = (sql) => {
    navigator.clipboard.writeText(sql).then(() => {
      // Brief visual feedback handled by CSS
    }).catch(() => {});
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0s';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return faSkullCrossbones;
      case 'high': return faExclamationCircle;
      case 'warning': return faExclamationTriangle;
      case 'medium': return faExclamationTriangle;
      case 'info': return faInfoCircle;
      case 'low': return faInfoCircle;
      default: return faCheckCircle;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'warning': return '#d97706';
      case 'medium': return '#d97706';
      case 'info': return '#3b82f6';
      case 'low': return '#6b7280';
      default: return '#10b981';
    }
  };

  if (loading) {
    return (
      <div className="temps-reel-loading">
        <div className="spinner"></div>
        <p>Chargement du tableau de bord...</p>
      </div>
    );
  }

  return (
    <div className="temps-reel-dashboard">
      {/* Header Section */}
      <header className="dashboard-header">
        <div className="header-title">
          <h1><FontAwesomeIcon icon={faTachometerAlt} /> Monitoring Serveur</h1>
          <span className="header-badge">
            <span className="pulse-dot"></span>
            {autoRefresh ? 'Live' : 'Pause'}
          </span>
          <p className="header-meta">Mis à jour: {lastUpdate.toLocaleTimeString()}</p>
        </div>
        <div className="header-controls">
          <div className="toggle-switch">
            <input 
              type="checkbox" 
              id="autoRefresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <label htmlFor="autoRefresh">Auto (10s)</label>
          </div>
          <button onClick={fetchRealtimeData} className="btn-refresh">
            <FontAwesomeIcon icon={faSync} spin={loading} />
          </button>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
        </div>
      )}

      {/* System Resources Grid */}
      <section className="resources-grid">
        {renderCircularProgress(
          parseFloat(serverPerformance.cpu?.usage || 0),
          "Processeur",
          `${serverPerformance.cpu?.cores || 0} Cores | Load: ${(serverPerformance.cpu?.load || 0).toFixed(2)}`,
          faMicrochip
        )}
        
        {renderCircularProgress(
          parseFloat(serverPerformance.memory?.percentage || 0),
          "Mémoire RAM",
          `${serverPerformance.memory?.usedGB || 0}GB / ${serverPerformance.memory?.totalGB || 0}GB`,
          faMemory
        )}

        {renderCircularProgress(
          parseFloat(serverPerformance.disk?.percentage || 0),
          "Disque Dur",
          `${serverPerformance.disk?.usedGB || 0}GB / ${serverPerformance.disk?.totalGB || 0}GB`,
          faHdd
        )}

        <div className="metric-card info-card">
          <div className="metric-header">
            <div className="icon-wrapper info">
              <FontAwesomeIcon icon={faServer} />
            </div>
            <h3>Système</h3>
          </div>
          <div className="info-list">
            <div className="info-item">
              <span>OS</span>
              <strong>{serverPerformance.distro || 'Linux'}</strong>
            </div>
            <div className="info-item">
              <span>Uptime</span>
              <strong>{formatUptime(serverPerformance.uptime)}</strong>
            </div>
            <div className="info-item">
              <span>Host</span>
              <strong>{serverPerformance.host}</strong>
            </div>
            <div className="info-item status-item">
              <span>Statut</span>
              <span className={`status-badge ${serverPerformance.status}`}>
                {serverPerformance.status === 'online' ? 'En Ligne' : 'Attention'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Active Users */}
      <section className="panel users-panel" ref={espaceViewRef}>
          <div className="panel-header">
            {usersViewMode === 'list' ? (
              <h2>
                <FontAwesomeIcon icon={faUsers} /> Utilisateurs Actifs ({activeUsers.length})
                <span className="online-count">
                  <span className="status-dot"></span>
                  En ligne ({activeUsers.filter(u => u.is_online).length})
                </span>
              </h2>
            ) : (
              <h2>
                <FontAwesomeIcon icon={faLayerGroup} /> Groupes Actifs ({espaces.length})
                <span className="online-count">
                  <span className="status-dot"></span>
                  Utilisateur en ligne ({new Set(espaces.flatMap(e => e.members.filter(m => m.is_online).map(m => m.id))).size})
                </span>
              </h2>
            )}
            <div className="users-view-toggle">
              <button
                className={`view-toggle-btn ${usersViewMode === 'list' ? 'active' : ''}`}
                onClick={() => setUsersViewMode('list')}
              >
                <FontAwesomeIcon icon={faList} /> Liste
              </button>
              <button
                className={`view-toggle-btn ${usersViewMode === 'espace' ? 'active' : ''}`}
                onClick={() => setUsersViewMode('espace')}
              >
                <FontAwesomeIcon icon={faLayerGroup} /> Par Espace
              </button>
            </div>
          </div>

          {usersViewMode === 'list' ? (
          <>
          {/* Search and Filters */}
          <div className="users-filters">
            <div className="search-box">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); setCurrentPage(1); }}
              />
            </div>
            <div className="filter-group">
              <FontAwesomeIcon icon={faFilter} className="filter-icon" />
              <select 
                value={statusFilter} 
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="all">Tous les statuts</option>
                <option value="online">En ligne</option>
                <option value="offline">Hors ligne</option>
              </select>
              <select 
                value={sourceFilter} 
                onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="all">Toutes les sources</option>
                <option value="web">Web</option>
                <option value="mobile">Mobile</option>
                <option value="electron">Desktop</option>
              </select>
            </div>
          </div>
          <div className="users-list-container">
            {(() => {
              // Filter users
              const filteredUsers = activeUsers.filter(user => {
                // Search filter
                const searchLower = userSearch.toLowerCase();
                const matchesSearch = !userSearch || 
                  (user.nom && user.nom.toLowerCase().includes(searchLower)) ||
                  (user.prenom && user.prenom.toLowerCase().includes(searchLower)) ||
                  (user.email && user.email.toLowerCase().includes(searchLower));
                
                // Status filter
                const matchesStatus = statusFilter === 'all' || 
                  (statusFilter === 'online' && user.is_online) ||
                  (statusFilter === 'offline' && !user.is_online);
                
                // Source filter
                const userSources = user.active_sources && user.active_sources.length > 0 
                  ? user.active_sources 
                  : [user.login_source || 'web'];
                const matchesSource = sourceFilter === 'all' || userSources.includes(sourceFilter);
                
                return matchesSearch && matchesStatus && matchesSource;
              });
              
              // Pagination
              const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
              const startIndex = (currentPage - 1) * USERS_PER_PAGE;
              const paginatedUsers = filteredUsers.slice(startIndex, startIndex + USERS_PER_PAGE);
              
              // Profile picture URL helper
              const getProfilePicUrl = (email) => {
                return `https://www.lonnii.com/uploads/lonnii-app/profile/${encodeURIComponent(email)}/profile.jpg`;
              };
              
              // Get initials
              const getInitials = (nom, prenom) => {
                const first = nom ? nom.charAt(0).toUpperCase() : '';
                const last = prenom ? prenom.charAt(0).toUpperCase() : '';
                return first + last || 'U';
              };
              
              // Format time ago
              const formatTimeAgo = (dateString) => {
                if (!dateString) return '';
                const now = new Date();
                const date = new Date(dateString);
                const diffMs = Math.abs(now - date);
                const diffSec = Math.floor(diffMs / 1000);
                const diffMin = Math.floor(diffSec / 60);
                const diffHour = Math.floor(diffMin / 60);
                const diffDay = Math.floor(diffHour / 24);
                
                if (diffSec < 60) return `${diffSec}s`;
                if (diffMin < 60) return `${diffMin}min`;
                if (diffHour < 24) {
                  const remainingMin = diffMin % 60;
                  return remainingMin > 0 ? `${diffHour}h ${remainingMin}min` : `${diffHour}h`;
                }
                if (diffDay < 30) {
                  const remainingHours = diffHour % 24;
                  return remainingHours > 0 ? `${diffDay}j ${remainingHours}h` : `${diffDay}j`;
                }
                return `${Math.floor(diffDay / 30)} mois`;
              };
              
              return (
                <>
                  {filteredUsers.length > 0 ? (
                    <>
                      <table className="users-table">
                        <thead>
                          <tr>
                            <th>Utilisateur</th>
                            <th>Email</th>
                            <th>Source</th>
                            <th>Statut</th>
                            <th>Dernière Activité</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedUsers.map((user, idx) => (
                            <React.Fragment key={idx}>
                            <tr className={`user-row ${expandedUserId === user.id ? 'expanded' : ''}`} onClick={() => toggleUserExpand(user)}>
                              <td>
                                <div className="user-cell">
                                  <div className="user-avatar-small">
                                    <img 
                                      src={getProfilePicUrl(user.email)} 
                                      alt=""
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                    <span className="avatar-initials" style={{ display: 'none' }}>
                                      {getInitials(user.nom, user.prenom)}
                                    </span>
                                  </div>
                                  <span>{user.nom} {user.prenom}</span>
                                </div>
                              </td>
                              <td className="email-cell">{user.email}</td>
                              <td>
                                <div className="sources-container">
                                  {(user.active_sources && user.active_sources.length > 0 ? user.active_sources : [user.login_source || 'web']).map((source, sourceIdx) => (
                                    <span key={sourceIdx} className={`source-badge ${source}`}>
                                      <FontAwesomeIcon 
                                        icon={source === 'mobile' ? faMobileAlt : (source === 'electron' || source === 'desktop' ? faLaptop : faDesktop)} 
                                        title={source === 'mobile' ? 'Application Mobile' : (source === 'electron' || source === 'desktop' ? 'Ordinateur' : 'Web')}
                                      />
                                      {source === 'mobile' ? ' Mobile' : (source === 'electron' || source === 'desktop' ? ' Ordinateur' : ' Web')}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td>
                                {user.is_online ? (
                                  <span className="status-badge online">
                                    <span className="status-text">
                                      <span className="status-dot"></span>
                                      EN LIGNE
                                    </span>
                                    <span className="status-time">Depuis {formatTimeAgo(user.last_seen)}</span>
                                  </span>
                                ) : (
                                  <span className="status-badge offline">
                                    <span className="status-text">HORS LIGNE</span>
                                    <span className="status-time">il y a {formatTimeAgo(user.last_seen)}</span>
                                  </span>
                                )}
                              </td>
                              <td>
                                <span className="time-badge">
                                  {user.last_activity ? new Date(user.last_activity).toLocaleDateString('fr-FR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  }) : 'Jamais'}
                                </span>
                              </td>
                            </tr>
                            {/* Expanded user detail row */}
                            {expandedUserId === user.id && (
                              <tr className="user-detail-row">
                                <td colSpan="5">
                                  <div className="user-detail-card">
                                    <div className="user-detail-grid">
                                      <div className="detail-item">
                                        <span className="detail-label">Email</span>
                                        <span className="detail-value">{user.email}</span>
                                      </div>
                                      <div className="detail-item">
                                        <span className="detail-label">ID Utilisateur</span>
                                        <span className="detail-value detail-id">{user.id}</span>
                                      </div>
                                      <div className="detail-item">
                                        <span className="detail-label">Source</span>
                                        <span className="detail-value">
                                          {(user.active_sources && user.active_sources.length > 0 ? user.active_sources : [user.login_source || 'web']).map(s => s === 'mobile' ? 'Mobile' : (s === 'electron' || s === 'desktop' ? 'Ordinateur' : 'Web')).join(', ')}
                                        </span>
                                      </div>
                                      <div className="detail-item">
                                        <span className="detail-label">Dernière activité</span>
                                        <span className="detail-value">
                                          {user.last_activity ? new Date(user.last_activity).toLocaleDateString('fr-FR', {
                                            day: '2-digit', month: '2-digit', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit'
                                          }) : 'Jamais'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="detail-groups">
                                      <span className="detail-label">Groupes connectés</span>
                                      {loadingUserGroups ? (
                                        <span className="detail-loading">Chargement...</span>
                                      ) : expandedUserGroups.length > 0 ? (
                                        <div className="detail-groups-list">
                                          {expandedUserGroups.map((g, gi) => (
                                            <span key={gi} className="detail-group-badge">
                                              <FontAwesomeIcon icon={faUsers} /> {g.nom}
                                              <small className="group-role">({g.user_role || 'membre'})</small>
                                            </span>
                                          ))}
                                        </div>
                                      ) : (
                                        <span className="detail-value text-muted">Aucun groupe</span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                      
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="pagination">
                          <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            <FontAwesomeIcon icon={faChevronLeft} />
                          </button>
                          <span className="pagination-info">
                            Page {currentPage} sur {totalPages} ({filteredUsers.length} utilisateurs)
                          </span>
                          <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                          >
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="empty-state">
                      <FontAwesomeIcon icon={faUserCircle} size="3x" />
                      <p>{userSearch || statusFilter !== 'all' || sourceFilter !== 'all' 
                        ? 'Aucun utilisateur ne correspond aux filtres' 
                        : 'Aucun utilisateur actif récemment'}</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          </>
          ) : (
            <div className="espace-view">
              <div className="espace-view-toolbar">
                <div className="search-box">
                  <FontAwesomeIcon icon={faSearch} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Rechercher un espace..."
                    value={espaceSearch}
                    onChange={(e) => { setEspaceSearch(e.target.value); setEspacePage(1); }}
                  />
                </div>
                <button className="btn-action" onClick={fetchEspacesData} title="Rafraîchir">
                  <FontAwesomeIcon icon={faSync} spin={loadingEspaces} />
                </button>
                <button className="btn-action" onClick={toggleFullscreen} title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}>
                  <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} /> {isFullscreen ? 'Quitter' : 'Plein écran'}
                </button>
              </div>

              {(() => {
                const filteredEspaces = espaces.filter(esp =>
                  !espaceSearch || (esp.nom && esp.nom.toLowerCase().includes(espaceSearch.toLowerCase()))
                );

                if (loadingEspaces && espaces.length === 0) {
                  return (
                    <div className="db-loading">
                      <div className="spinner small"></div>
                      <span>Chargement des espaces...</span>
                    </div>
                  );
                }

                if (filteredEspaces.length === 0) {
                  return (
                    <div className="empty-state">
                      <FontAwesomeIcon icon={faLayerGroup} size="3x" />
                      <p>{espaceSearch ? 'Aucun espace ne correspond à la recherche' : 'Aucun espace trouvé'}</p>
                    </div>
                  );
                }

                const getInitials = (nom, prenom) => {
                  const first = nom ? nom.charAt(0).toUpperCase() : '';
                  const last = prenom ? prenom.charAt(0).toUpperCase() : '';
                  return first + last || 'U';
                };

                const totalPages = Math.ceil(filteredEspaces.length / ESPACES_PER_PAGE);
                const startIndex = (espacePage - 1) * ESPACES_PER_PAGE;
                const paginatedEspaces = filteredEspaces.slice(startIndex, startIndex + ESPACES_PER_PAGE);

                return (
                  <>
                  <div className="espace-grid">
                    {paginatedEspaces.map((esp) => (
                      <div key={esp.id} className="espace-card">
                        <div className="espace-card-header">
                          <span className="espace-name">
                            <FontAwesomeIcon icon={faLayerGroup} />
                            <span className="espace-name-text" title={esp.nom}>{esp.nom}</span>
                          </span>
                          <span className={`espace-online-badge ${esp.onlineCount === 0 ? 'offline' : ''}`}>
                            <span className="status-dot"></span>
                            {esp.onlineCount} en ligne
                          </span>
                        </div>
                        <div className="espace-members-list">
                          {esp.members.map((member) => (
                            <div key={member.id} className={`espace-member ${member.is_online ? 'online' : 'offline'}`}>
                              <div className="espace-avatar-wrap">
                                <div className="user-avatar-small">
                                  <img
                                    src={`https://www.lonnii.com/uploads/lonnii-app/profile/${encodeURIComponent(member.email)}/profile.jpg`}
                                    alt=""
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                  <span className="avatar-initials" style={{ display: 'none' }}>
                                    {getInitials(member.nom, member.prenom)}
                                  </span>
                                </div>
                                <span className={`member-status-dot ${member.is_online ? 'online' : 'offline'}`}></span>
                              </div>
                              <div className="espace-member-info">
                                <span className="espace-member-name">{member.nom} {member.prenom}</span>
                                <span className="espace-member-email">{member.email}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => setEspacePage(p => Math.max(1, p - 1))}
                        disabled={espacePage === 1}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      <span className="pagination-info">
                        Page {espacePage} sur {totalPages} ({filteredEspaces.length} espaces)
                      </span>
                      <button
                        className="pagination-btn"
                        onClick={() => setEspacePage(p => Math.min(totalPages, p + 1))}
                        disabled={espacePage === totalPages}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                  )}
                  </>
                );
              })()}
            </div>
          )}
      </section>

      {/* Live Visitors (Accueil page) */}
      <section className="panel">
        <div className="panel-header">
          <h2>
            <FontAwesomeIcon icon={faGlobe} /> Visiteurs Accueil en Direct
            <span className="header-badge" style={{ marginLeft: '0.75rem' }}>
              <span className="pulse-dot"></span>
              Live
            </span>
          </h2>
        </div>
        {liveVisits.length > 0 ? (
          <table className="users-table">
            <thead>
              <tr>
                <th>Pays</th>
                <th>Région</th>
                <th>Ville</th>
                <th>Page</th>
                <th>Heure</th>
              </tr>
            </thead>
            <tbody>
              {liveVisits.map((visit) => (
                <tr key={visit.id}>
                  <td>{visit.country || 'Inconnu'}</td>
                  <td>{visit.region || '-'}</td>
                  <td>{visit.city || '-'}</td>
                  <td>{visit.path || '/'}</td>
                  <td>
                    {visit.visited_at
                      ? new Date(visit.visited_at).toLocaleTimeString('fr-FR')
                      : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <FontAwesomeIcon icon={faGlobe} size="3x" />
            <p>Aucune visite enregistrée récemment</p>
          </div>
        )}
      </section>

      {/* ═══ Auto-Guard Alert Banner ═══ */}
      {autoGuardAlert && (
        <div className={`auto-guard-alert ${autoGuardAlert.level}`}>
          <FontAwesomeIcon icon={autoGuardAlert.level === 'critical' ? faSkullCrossbones : faExclamationTriangle} />
          <span>{autoGuardAlert.message}</span>
          <button onClick={() => setAutoGuardAlert(null)} className="alert-dismiss">✕</button>
        </div>
      )}

      {/* ═══ DB Management Section ═══ */}
      <section className="panel db-management-panel">
        <div className="panel-header">
          <h2><FontAwesomeIcon icon={faWrench} /> Gestion Base de Données</h2>
          <div className="panel-actions">
            <button onClick={optimizeDatabase} className="btn-action" title="Optimiser">
              <FontAwesomeIcon icon={faBolt} /> Optimiser
            </button>
            <button onClick={clearCache} className="btn-action" title="Vider Cache">
              <FontAwesomeIcon icon={faBroom} /> Cache
            </button>
            <div className="auto-guard-toggle">
              <button 
                onClick={() => setAutoGuardEnabled(!autoGuardEnabled)}
                className={`btn-action ${autoGuardEnabled ? 'active' : ''}`}
                title="Protection automatique des connexions"
              >
                <FontAwesomeIcon icon={autoGuardEnabled ? faToggleOn : faToggleOff} />
                {' '}Auto-Guard {autoGuardEnabled ? 'ON' : 'OFF'}
              </button>
              {autoGuardEnabled && (
                <div className="guard-settings">
                  <label>
                    Seuil: 
                    <select value={autoGuardThreshold} onChange={(e) => setAutoGuardThreshold(parseInt(e.target.value))}>
                      <option value={50}>50%</option>
                      <option value={60}>60%</option>
                      <option value={70}>70%</option>
                      <option value={80}>80%</option>
                    </select>
                  </label>
                  <label>
                    Idle max: 
                    <select value={autoGuardIdleMinutes} onChange={(e) => setAutoGuardIdleMinutes(parseInt(e.target.value))}>
                      <option value={2}>2 min</option>
                      <option value={3}>3 min</option>
                      <option value={5}>5 min</option>
                      <option value={10}>10 min</option>
                    </select>
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="db-tabs">
          {[
            { key: 'overview', label: 'Vue d\'ensemble', icon: faDatabase },
            { key: 'indexes', label: 'Index', icon: faListAlt },
            { key: 'diagnostics', label: 'Diagnostics', icon: faStethoscope },
            { key: 'connections', label: 'Connexions', icon: faNetworkWired }
          ].map(tab => (
            <button
              key={tab.key}
              className={`db-tab ${dbManagementTab === tab.key ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.key)}
            >
              <FontAwesomeIcon icon={tab.icon} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="db-tab-content">
          {loadingDbManagement && (
            <div className="db-loading">
              <div className="spinner small"></div>
              <span>Analyse en cours...</span>
            </div>
          )}

          {/* ── Overview Tab ── */}
          {dbManagementTab === 'overview' && (
            <div className="db-overview">
              <div className="overview-cards">
                <div className="overview-card">
                  <div className="ov-icon" style={{ background: '#eff6ff', color: '#3b82f6' }}>
                    <FontAwesomeIcon icon={faNetworkWired} />
                  </div>
                  <div className="ov-info">
                    <span className="ov-value">{dbPerformance.connections?.active || 0} / {dbPerformance.connections?.max || 100}</span>
                    <span className="ov-label">Connexions</span>
                  </div>
                  <div className="ov-bar">
                    <div className="ov-bar-fill" style={{ 
                      width: `${dbPerformance.connections?.usage || 0}%`,
                      background: parseFloat(dbPerformance.connections?.usage || 0) > 70 ? '#ef4444' : parseFloat(dbPerformance.connections?.usage || 0) > 50 ? '#f59e0b' : '#10b981'
                    }}></div>
                  </div>
                </div>

                <div className="overview-card">
                  <div className="ov-icon" style={{ background: '#ecfdf5', color: '#10b981' }}>
                    <FontAwesomeIcon icon={faShieldAlt} />
                  </div>
                  <div className="ov-info">
                    <span className="ov-value">{dbPerformance.cache?.hitRatio || 0}%</span>
                    <span className="ov-label">Cache Hit Ratio</span>
                  </div>
                  <div className="ov-bar">
                    <div className="ov-bar-fill success" style={{ width: `${dbPerformance.cache?.hitRatio || 0}%` }}></div>
                  </div>
                </div>

                <div className="overview-card">
                  <div className="ov-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                  </div>
                  <div className="ov-info">
                    <span className="ov-value">{dbPerformance.queries?.slow || 0}</span>
                    <span className="ov-label">Requêtes Lentes</span>
                  </div>
                </div>

                <div className="overview-card">
                  <div className="ov-icon" style={{ background: '#f3e8ff', color: '#7c3aed' }}>
                    <FontAwesomeIcon icon={faHdd} />
                  </div>
                  <div className="ov-info">
                    <span className="ov-value">{dbPerformance.size?.total || '0 MB'}</span>
                    <span className="ov-label">Taille DB</span>
                  </div>
                </div>

                <div className="overview-card">
                  <div className="ov-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div className="ov-info">
                    <span className="ov-value">
                      {dbPerformance.queries?.avgTime != null
                        ? `${dbPerformance.queries.avgTime} ms`
                        : `${queryMetrics.fetchTime || 0} ms`}
                    </span>
                    <span className="ov-label">
                      {dbPerformance.queries?.avgTime != null ? 'Temps Moy. Requête' : 'Temps Réponse API'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Auto-Guard Status */}
              <div className={`guard-status ${autoGuardEnabled ? 'active' : 'inactive'}`}>
                <div className="guard-status-header">
                  <FontAwesomeIcon icon={faShieldAlt} />
                  <strong>Auto-Guard: {autoGuardEnabled ? 'Activé' : 'Désactivé'}</strong>
                </div>
                {autoGuardEnabled && (
                  <p>Nettoyage automatique quand les connexions dépassent {autoGuardThreshold}% (idle &gt; {autoGuardIdleMinutes}min). Vérification toutes les 30s.</p>
                )}
              </div>

              {/* Top Tables */}
              {dbPerformance.size?.tables?.length > 0 && (
                <div className="top-tables">
                  <h4>Top Tables par Taille</h4>
                  <div className="tables-list">
                    {dbPerformance.size.tables.map((t, i) => (
                      <div key={i} className="table-row">
                        <span className="table-rank">#{i + 1}</span>
                        <span className="table-name">{t.name}</span>
                        <span className="table-size">{t.size}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="overview-actions">
                <button onClick={() => handleTabChange('indexes')} className="btn-action">
                  <FontAwesomeIcon icon={faListAlt} /> Analyser les Index
                </button>
                <button onClick={() => handleTabChange('diagnostics')} className="btn-action">
                  <FontAwesomeIcon icon={faStethoscope} /> Lancer le Diagnostic
                </button>
                <button onClick={() => handleTabChange('connections')} className="btn-action">
                  <FontAwesomeIcon icon={faNetworkWired} /> Voir les Connexions
                </button>
              </div>
            </div>
          )}

          {/* ── Index Recommendations Tab ── */}
          {dbManagementTab === 'indexes' && !loadingDbManagement && (
            <div className="db-indexes">
              {/* Summary bar */}
              <div className="reco-summary">
                <div className="reco-count total">{indexRecommendations.summary?.total || 0} recommandations</div>
                {(indexRecommendations.summary?.highSeverity || 0) > 0 && (
                  <div className="reco-count high">{indexRecommendations.summary.highSeverity} haute priorité</div>
                )}
                {(indexRecommendations.summary?.mediumSeverity || 0) > 0 && (
                  <div className="reco-count medium">{indexRecommendations.summary.mediumSeverity} moyenne</div>
                )}
                {(indexRecommendations.summary?.lowSeverity || 0) > 0 && (
                  <div className="reco-count low">{indexRecommendations.summary.lowSeverity} basse</div>
                )}
                <button onClick={fetchIndexRecommendations} className="btn-action btn-sm">
                  <FontAwesomeIcon icon={faSync} /> Rafraîchir
                </button>
              </div>

              {indexRecommendations.recommendations?.length > 0 ? (
                <div className="reco-list">
                  {indexRecommendations.recommendations.map((rec, i) => (
                    <div key={i} className={`reco-card severity-${rec.severity}`}>
                      <div className="reco-header">
                        <FontAwesomeIcon icon={getSeverityIcon(rec.severity)} style={{ color: getSeverityColor(rec.severity) }} />
                        <div className="reco-title-group">
                          <span className="reco-table">{rec.table}</span>
                          <span className={`severity-badge ${rec.severity}`}>{rec.severity}</span>
                          <span className={`type-badge ${rec.type}`}>
                            {rec.type === 'missing_index' ? 'Index Manquant' : 
                             rec.type === 'missing_fk_index' ? 'FK sans Index' :
                             rec.type === 'unused_index' ? 'Index Inutilisé' : 'Index Doublon'}
                          </span>
                        </div>
                      </div>
                      <p className="reco-reason">{rec.reason}</p>
                      <p className="reco-suggestion"><strong>💡</strong> {rec.suggestion}</p>
                      {rec.stats && (
                        <div className="reco-stats">
                          <span>Seq scans: {rec.stats.seqScans?.toLocaleString()}</span>
                          <span>Lignes: {rec.stats.rowCount?.toLocaleString()}</span>
                          <span>Taille: {rec.stats.tableSize}</span>
                        </div>
                      )}
                      <div className="reco-sql-block">
                        <code>{rec.sql}</code>
                        <div className="reco-actions">
                          <button onClick={() => copySql(rec.sql)} className="btn-action btn-sm" title="Copier">
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                          {(rec.type === 'missing_fk_index' || rec.type === 'unused_index') && !rec.sql.startsWith('--') && (
                            <button 
                              onClick={() => executeIndexCommand(rec.sql.split(';')[0] + ';')} 
                              className="btn-action btn-sm btn-execute"
                              disabled={executingAction === rec.sql}
                              title="Exécuter"
                            >
                              <FontAwesomeIcon icon={faPlay} /> Exécuter
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state success-state">
                  <FontAwesomeIcon icon={faCheckCircle} size="3x" style={{ color: '#10b981' }} />
                  <p>Aucune recommandation d'index — Votre base est bien optimisée!</p>
                </div>
              )}
            </div>
          )}

          {/* ── Performance Diagnostics Tab ── */}
          {dbManagementTab === 'diagnostics' && !loadingDbManagement && (
            <div className="db-diagnostics">
              {/* Summary */}
              <div className="diag-summary">
                <div className={`diag-status ${performanceDiagnostics.summary?.status || 'healthy'}`}>
                  <FontAwesomeIcon icon={
                    performanceDiagnostics.summary?.status === 'critical' ? faSkullCrossbones :
                    performanceDiagnostics.summary?.status === 'warning' ? faExclamationTriangle : faCheckCircle
                  } />
                  <span>
                    {performanceDiagnostics.summary?.status === 'critical' ? 'Problèmes Critiques Détectés' :
                     performanceDiagnostics.summary?.status === 'warning' ? 'Attention Requise' : 'Système en Bonne Santé'}
                  </span>
                </div>
                <div className="diag-counts">
                  {(performanceDiagnostics.summary?.critical || 0) > 0 && (
                    <span className="diag-count critical">{performanceDiagnostics.summary.critical} critique(s)</span>
                  )}
                  {(performanceDiagnostics.summary?.warning || 0) > 0 && (
                    <span className="diag-count warning">{performanceDiagnostics.summary.warning} avertissement(s)</span>
                  )}
                  {(performanceDiagnostics.summary?.info || 0) > 0 && (
                    <span className="diag-count info">{performanceDiagnostics.summary.info} info(s)</span>
                  )}
                </div>
                <button onClick={fetchPerformanceDiagnostics} className="btn-action btn-sm">
                  <FontAwesomeIcon icon={faSync} /> Re-diagnostiquer
                </button>
              </div>

              {/* Connection Health Breakdown */}
              {performanceDiagnostics.connectionHealth?.length > 0 && (
                <div className="conn-health-grid">
                  <h4>État des Connexions</h4>
                  <div className="conn-health-bars">
                    {performanceDiagnostics.connectionHealth.map((ch, i) => (
                      <div key={i} className={`conn-health-item state-${(ch.state || 'unknown').replace(/\s+/g, '-')}`}>
                        <span className="ch-state">{ch.state || 'unknown'}</span>
                        <span className="ch-count">{ch.count}</span>
                        <span className="ch-avg">moy: {formatDuration(ch.avgDuration)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Diagnostics List */}
              {performanceDiagnostics.diagnostics?.length > 0 ? (
                <div className="diag-list">
                  {performanceDiagnostics.diagnostics.map((diag, i) => (
                    <div key={i} className={`diag-card severity-${diag.severity}`}>
                      <div className="diag-header">
                        <FontAwesomeIcon icon={getSeverityIcon(diag.severity)} style={{ color: getSeverityColor(diag.severity) }} />
                        <span className="diag-title">{diag.title}</span>
                        <span className={`severity-badge ${diag.severity}`}>{diag.severity}</span>
                        <span className={`category-badge ${diag.category}`}>
                          {diag.category === 'bloat' ? '🗑️ Bloat' : 
                           diag.category === 'slow_query' ? '🐌 Requête' :
                           diag.category === 'lock' ? '🔒 Verrou' :
                           diag.category === 'config' ? '⚙️ Config' : '🔌 Connexions'}
                        </span>
                      </div>
                      <p className="diag-description">{diag.description}</p>
                      {diag.details?.deadRatio != null && (
                        <div className="diag-details">
                          <span>Vivantes: {diag.details.liveTuples?.toLocaleString()}</span>
                          <span>Mortes: {diag.details.deadTuples?.toLocaleString()}</span>
                          <span>Ratio mort: {diag.details.deadRatio}%</span>
                          <span>Taille: {diag.details.tableSize}</span>
                          <span>Dernier VACUUM: {diag.details.lastVacuum === 'Jamais' ? '⚠️ Jamais' : new Date(diag.details.lastVacuum).toLocaleDateString('fr-FR')}</span>
                        </div>
                      )}
                      <div className="diag-solution">
                        <strong>Solution:</strong>
                        <code>{diag.solution}</code>
                        <div className="diag-actions">
                          <button onClick={() => copySql(diag.solution)} className="btn-action btn-sm" title="Copier">
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                          {(diag.category === 'bloat' || diag.category === 'slow_query' || diag.category === 'lock' || diag.category === 'config') && (
                            <button 
                              onClick={() => executeDiagnosticFix(diag.solution, diag.category)} 
                              className="btn-action btn-sm btn-execute"
                              disabled={executingAction === diag.solution}
                              title="Appliquer"
                            >
                              <FontAwesomeIcon icon={faPlay} /> Appliquer
                            </button>
                          )}
                          {diag.category === 'connections' && (
                            <button 
                              onClick={() => cleanupIdleConnections(diag.details?.avgDuration ?? 30)} 
                              className="btn-action btn-sm btn-execute"
                              disabled={executingAction === 'cleanup'}
                              title="Nettoyer les connexions inactives"
                            >
                              <FontAwesomeIcon icon={faPlay} /> Appliquer
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state success-state">
                  <FontAwesomeIcon icon={faCheckCircle} size="3x" style={{ color: '#10b981' }} />
                  <p>Aucun problème détecté — Performances optimales!</p>
                </div>
              )}

              {/* PG Config Summary */}
              {performanceDiagnostics.config?.length > 0 && (
                <div className="pg-config-section">
                  <h4><FontAwesomeIcon icon={faWrench} /> Configuration PostgreSQL</h4>
                  <div className="config-grid">
                    {performanceDiagnostics.config.map((c, i) => (
                      <div key={i} className="config-item">
                        <span className="config-name">{c.name}</span>
                        <span className="config-value">{c.value}{c.unit ? <span className="config-unit"> {c.unit}</span> : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Connections Detail Tab ── */}
          {dbManagementTab === 'connections' && !loadingDbManagement && (
            <div className="db-connections">
              {/* Connection Summary */}
              <div className="conn-summary">
                <div className="conn-summary-main">
                  <span className="conn-total">
                    {connectionsDetail.summary?.total || 0} / {connectionsDetail.summary?.max || 100}
                  </span>
                  <span className="conn-usage">
                    ({connectionsDetail.summary?.usage || 0}% utilisé)
                  </span>
                </div>
                <div className="conn-by-state">
                  {connectionsDetail.summary?.byState && Object.entries(connectionsDetail.summary.byState).map(([state, count]) => (
                    <span key={state} className={`conn-state-badge state-${state.replace(/\s+/g, '-')}`}>
                      {state}: {count}
                    </span>
                  ))}
                </div>
                <div className="conn-actions-top">
                  <button onClick={fetchConnectionsDetail} className="btn-action btn-sm">
                    <FontAwesomeIcon icon={faSync} /> Rafraîchir
                  </button>
                  <button onClick={cleanupIdleConnections} className="btn-action btn-sm btn-danger" disabled={executingAction === 'cleanup'}>
                    <FontAwesomeIcon icon={faTrashAlt} /> Nettoyer Idle ({autoGuardIdleMinutes}min+)
                  </button>
                </div>
              </div>

              {/* Connections Table */}
              {connectionsDetail.connections?.length > 0 ? (
                <div className="conn-table-wrapper">
                  <table className="conn-table">
                    <thead>
                      <tr>
                        <th>PID</th>
                        <th>Utilisateur</th>
                        <th>État</th>
                        <th>Durée État</th>
                        <th>App</th>
                        <th>Requête</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connectionsDetail.connections.map((conn, i) => (
                        <tr key={i} className={`conn-row state-${(conn.state || 'unknown').replace(/\s+/g, '-')}`}>
                          <td className="conn-pid">{conn.pid}</td>
                          <td>{conn.username}</td>
                          <td>
                            <span className={`conn-state-dot ${conn.state === 'active' ? 'active' : conn.state === 'idle' ? 'idle' : 'other'}`}></span>
                            {conn.state}
                          </td>
                          <td>{formatDuration(conn.stateDuration)}</td>
                          <td className="conn-app">{conn.applicationName || '-'}</td>
                          <td className="conn-query" title={conn.query}>
                            {conn.query ? (conn.query.length > 60 ? conn.query.substring(0, 60) + '...' : conn.query) : '-'}
                          </td>
                          <td>
                            <button 
                              onClick={() => killConnection(conn.pid)} 
                              className="btn-kill"
                              disabled={executingAction === `kill-${conn.pid}`}
                              title={`Terminer PID ${conn.pid}`}
                            >
                              <FontAwesomeIcon icon={faTimesCircle} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <FontAwesomeIcon icon={faNetworkWired} size="3x" />
                  <p>Aucune connexion active</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TempsReel;
